// Nurse3ActivePatientCard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import type { OutletCtx } from "../layout/AppLayout";
import api from "../../../api/axiosConfig";
import "../../../center admin css/LabReportsEntry.css";
import "../../../center admin css/centerAdmin.css";

/** ====== API types ====== */
type WaitingItem = {
  _id: string;
  createdAt: string;
  sessionStartAt?: string;
  startedAt?: string;
  stationId: string;
  sessionId?: string;
  session_id?: string;
  session?: { id?: string; sessionId?: string };
  child: {
    childId: string;
    name: string;
    age: number;
    gender: string;
    emrId: string;
    profile?: string;
  };
};

type StationPatientsResp = {
  waiting?: WaitingItem[];
  inProgress?: WaitingItem[];
  completed?: WaitingItem[];
};

interface ActivePatientCardProps {
  activePatient?: any;
  onPatientSelect?: (patient: {
    id: string;
    name: string;
    age: number;
    gender: string;
    emrId?: string;
  }) => void;
  showActions?: boolean;
  sessionId?: string;
  stationId?: string;
  // ✅ ADD THIS
  requiredForms?: string[];
  completedForms?: string[];
  validateBeforeComplete?: () => boolean;
  formStatus?: {
    buSample: boolean;
  };
  onStationStarted?: () => void;
}

function readSessionIdFromItem(it?: WaitingItem): string | undefined {
  if (!it) return undefined;
  return (
    it.sessionId ||
    (it as any).session_id ||
    it.session?.id ||
    it.session?.sessionId ||
    undefined
  );
}

function readSessionIdFromStorage(): string | undefined {
  return (
    sessionStorage.getItem("workflowSessionId") ||
    sessionStorage.getItem("lastSessionId") ||
    sessionStorage.getItem("currentSessionId") ||
    undefined
  );
}

function clearStoredSessionIds() {
  ["workflowSessionId", "lastSessionId", "currentSessionId"].forEach((k) =>
    sessionStorage.removeItem(k),
  );
}

type Queues = {
  waiting: WaitingItem[];
  inProgress: WaitingItem[];
  completed: WaitingItem[];
};

const Nurse3ActivePatientCard: React.FC<ActivePatientCardProps> = ({
  activePatient: propPatient,
  onPatientSelect,
  showActions = false,
  sessionId,
  stationId = "NURSE3",
  validateBeforeComplete, // ✅ ADD THIS
  formStatus,
  onStationStarted,
}) => {
  const outlet = useOutletContext<any>();

  const { setActivePatient } = outlet;

  const [loading, setLoading] = useState(false);
  // ✅ NEW: State for avatar
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();

  const [patients, setPatients] = useState<Queues>({
    waiting: [],
    inProgress: [],
    completed: [],
  });
  const [effectiveSessionId, setEffectiveSessionId] = useState<string>();

  const loadQueue = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found. Please log in.");
      const headers = { Authorization: `Bearer ${token}` };

      const centerId =
        sessionStorage.getItem("centerId") || localStorage.getItem("centerId");

      const effectiveDate = new Date().toISOString().slice(0, 10);

      const { data } = await api.centerworkflowApi.get(
        `/queues/NURSE3_Q?center_id=${encodeURIComponent(
          centerId || "",
        )}&date=${encodeURIComponent(effectiveDate)}`,
        { headers },
      );

      setPatients({
        waiting: [],
        inProgress: data?.inProgress ?? [],
        completed: [],
      });
    } catch (e) {
      console.warn("Nurse3ActivePatientCard: failed to load station queue", e);
      setPatients({ waiting: [], inProgress: [], completed: [] });
    }
  };

  useEffect(() => {
    loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  // const current =
  //   propPatient ||
  //   outlet?.activePatient ||
  //   (() => {
  //     try {
  //       const stored = sessionStorage.getItem("selectedPatient_NURSE3_Q");

  //       if (!stored) return null;

  //       const parsed = JSON.parse(stored);

  //       return {
  //         _id: parsed._id || parsed.id,
  //         child: {
  //           childId: parsed.emrId,
  //           emrId: parsed.emrId,
  //           name: parsed.name,
  //           gender: parsed.genderShort === "F" ? "Female" : "Male",
  //           age: parsed.ageYears,
  //         },
  //       };
  //     } catch {
  //       return null;
  //     }
  //   })();

  // const current = useMemo(() => {

  //   if (propPatient?._id) {
  //     return propPatient;
  //   }

  //   if (outlet?.activePatient?._id) {
  //     return outlet.activePatient;
  //   }

  //   // if (patients.inProgress.length > 0) {
  //   //   return patients.inProgress[0];
  //   // }

  //   try {
  //     const stored = sessionStorage.getItem("selectedPatient_NURSE3_Q");

  //     if (!stored) return null;

  //     const parsed = JSON.parse(stored);

  //     return {
  //       _id: parsed._id || parsed.id,
  //       child: {
  //         childId: parsed.emrId,
  //         emrId: parsed.emrId,
  //         name: parsed.name,
  //         gender: parsed.genderShort === "F" ? "Female" : "Male",
  //         age: parsed.ageYears,
  //       },
  //     };
  //   } catch {
  //     return null;
  //   }
  //   console.log("CURRENT PATIENT", current);
  // }, [propPatient, outlet?.activePatient, patients.inProgress]);

  const activePatient = outlet?.activePatient ?? null;

  const current = useMemo(() => {
    // Priority 1 → current patient from AppLayout
    if (activePatient?._id) {
      return activePatient;
    }

    // Priority 2 → queue in progress
    if (patients?.inProgress?.length) {
      return patients.inProgress[0];
    }

    return null;
  }, [activePatient, patients]);

  useEffect(() => {
    const fromItem = readSessionIdFromItem(current);
    const fromStore = readSessionIdFromStorage();
    const resolved = sessionId || fromItem || fromStore;
    setEffectiveSessionId(resolved);
  }, [sessionId, current]);

  /* ================= IMAGE FETCH LOGIC (FIXED) ================= */

  // useEffect(() => {
  //   const loadAvatar = async () => {
  //     // 1. Reset logic
  //     setAvatarUrl(undefined);

  //     if (!current?.child?.childId) return;

  //     try {
  //       const token = sessionStorage.getItem("accessToken");
  //       if (!token) return;
  //       const headers = { Authorization: `Bearer ${token}` };

  //       const centerId =
  //         sessionStorage.getItem("centerId") ||
  //         localStorage.getItem("centerId");

  //       if (!centerId) return;

  //       const effectiveDate = new Date().toISOString().slice(0, 10);

  //       const apptRes = await api.appointment_api.get(
  //         `/api/appointments/center/${encodeURIComponent(
  //           centerId,
  //         )}/date?date=${encodeURIComponent(effectiveDate)}`,
  //         { headers },
  //       );

  //       const bookings = apptRes.data?.bookings || [];

  //       // Robust match
  //       const targetId = current.child.childId.trim();
  //       const match = bookings.find(
  //         (b: any) => b.child_id && b.child_id.trim() === targetId,
  //       );

  //       const filePath = match?.child_profile;
  //       if (!filePath) return;

  //       // Optimization: if signed, set immediately
  //       if (filePath.includes("X-Amz-Signature")) {
  //         setAvatarUrl(filePath);
  //         return;
  //       }

  //       // ✅ FIX: Manual string splitting instead of new URL()
  //       const parts = filePath.split("/");

  //       // Safety check
  //       if (parts.length < 3) {
  //         console.warn("Invalid file path format:", filePath);
  //         return;
  //       }

  //       // Extract Module
  //       const module = `${parts[0]}/${parts[1]}`;

  //       // Extract Key
  //       const key = parts.slice(2).join("/");

  //       const img = await api.S3config.get("/view-url", {
  //         params: { module, key },
  //         headers,
  //       });

  //       const signedUrl = img.data?.url || (img as any).url;

  //       if (signedUrl) {
  //         setAvatarUrl(signedUrl);
  //       }
  //     } catch (err) {
  //       console.error("Nurse3 avatar load failed", err);
  //     }
  //   };

  //   loadAvatar();
  // }, [current?.child?.emrId]);
  /* ================================================= */

  /* ================= IMAGE FETCH LOGIC ================= */
  useEffect(() => {
    const loadAvatar = async () => {
      setAvatarUrl(undefined);

      const profileKey = current?.child?.profile;

      if (!profileKey) return;

      try {
        const token = sessionStorage.getItem("accessToken");
        if (!token) return;

        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // Already a presigned URL
        if (profileKey.includes("X-Amz-Signature")) {
          setAvatarUrl(profileKey);
          return;
        }

        // Remove leading slash if present
        const cleanPath = profileKey.startsWith("/")
          ? profileKey.slice(1)
          : profileKey;

        const parts = cleanPath.split("/");

        if (parts.length < 3) {
          console.warn("Invalid profile key:", profileKey);
          return;
        }

        const module = `${parts[0]}/${parts[1]}`;
        const key = parts.slice(2).join("/");

        const img = await api.S3config.get("/view-url", {
          params: {
            module,
            key,
          },
          headers,
        });

        const signedUrl = img.data?.url || (img as any).url;

        if (signedUrl) {
          setAvatarUrl(signedUrl);
        }
      } catch (err) {
        console.error("Nurse3 avatar load failed", err);
      }
    };

    loadAvatar();
  }, [current?.child?.childId, current?.child?.profile]);
  /* ================================================= */

  /* ================================================= */

  useEffect(() => {
    if (!current) return;
    onPatientSelect?.({
      id: current._id,
      name: current.child?.name ?? "",
      age: Number(current.child?.age ?? 0),
      gender: String(current.child?.gender || "M")
        .toUpperCase()
        .startsWith("F")
        ? "Female"
        : "Male",
      emrId: current.child?.childId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?._id]);

  const displayAge = current?.child?.age ? `${current.child.age} Years` : "-";

  const displayGender = current?.child?.gender
    ? String(current.child.gender).toUpperCase().startsWith("F")
      ? "Female"
      : "Male"
    : "-";

  // const doPost = async (path: "start" | "complete") => {
  const doPost = async (path: "start" | "complete" | "cancel") => {
    // if (!effectiveSessionId || !stationId) {
    const sessionId = effectiveSessionId || sessionStorage.getItem("sessionId");

    if (!sessionId || !stationId) {
      alert(
        "No active session id found. Pick a patient or start a new session.",
      );
      return;
    }
    try {
      setLoading(true);
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found.");
      const headers = { Authorization: `Bearer ${token}` };

      let url = "";

      if (path === "cancel") {
        url = `/sessions/${encodeURIComponent(
          sessionId,
        )}/stations/NURSE3/cancel`;
      } else if (path === "complete") {
        url = `/sessions/${encodeURIComponent(
          sessionId,
        )}/stations/NURSE3/complete`;
      } else {
        url = `/sessions/${encodeURIComponent(
          sessionId,
        )}/stations/${encodeURIComponent(stationId)}/start`;
      }

      let payload: any = {};

      if (path === "complete") {
        const completedForms: string[] = ["ORAL_IMAGING", "SALIVA_SAMPLE"];

        if (formStatus?.buSample) {
          completedForms.push("B_AND_U_SAMPLE");
        }

        payload = {
          completedForms,
          payload: {},
        };
      }

      const res = await api.centerworkflowApi.post(url, payload, { headers });

      if (path === "start") {
        alert(`Station "${stationId}" started successfully`);
        onStationStarted?.(); // ✅ CALL THIS
        window.dispatchEvent(new Event("patientUpdated")); // ✅ ADD THIS
      } else {
        if (res.data?.isComplete) {
          clearStoredSessionIds();
          setActivePatient?.(null);
          sessionStorage.removeItem("selectedPatient_NURSE3_Q");

          alert("🎉 Workflow completed for this session");
        } else if (res.data?.nextStation) {
          clearStoredSessionIds();
          setActivePatient?.(null);
          sessionStorage.removeItem("selectedPatient_NURSE3_Q");

          alert(`Next station: ${res.data.nextStation}`);
        } else {
          clearStoredSessionIds();
          setActivePatient?.(null);
          sessionStorage.removeItem("selectedPatient_NURSE3_Q");

          alert("Station completed");
        }

        window.dispatchEvent(new Event("patientUpdated"));
      }

      await loadQueue();
      window.dispatchEvent(new Event("patientUpdated"));
    } catch (err) {
      console.error(`❌ Error on ${path}:`, err);
      alert(`Failed to ${path} station`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="patient-details-card" style={{ marginBottom: 18 }}>
      <div className="patient-details-top">
        {/* ✅ NEW: Display Avatar */}
        <div className="patient-avatar-large" style={{ overflow: "hidden" }}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="Patient"
              className="patient-avatar-img"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div className="patient-avatar-fallback" />
          )}
        </div>

        <div className="patient-info-group">
          <div className="patient-info-row">
            <div>
              <span className="field-label">EMR ID</span>
              <div className="field-value">
                {current?.child?.childId ?? "-"}
              </div>
            </div>
            <div>
              <span className="field-label">Name</span>
              <div className="field-value">{current?.child?.name ?? "-"}</div>
            </div>
          </div>

          <div className="patient-info-row">
            <div>
              <span className="field-label">Age</span>
              <div className="field-value">{displayAge}</div>
            </div>
            <div>
              <span className="field-label">Gender</span>
              <div className="field-value">{displayGender}</div>
            </div>
          </div>
        </div>
      </div>

      {showActions && (
        <div
          className="patient-action-buttons"
          style={{ marginTop: 16, display: "flex", gap: "12px" }}
        >
          <button
            className="start-btn"
            style={{
              backgroundColor: "#4CAF50",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => current && doPost("start")}
            disabled={loading || !current}
          >
            {loading ? "Starting..." : "Start"}
          </button>

          <button
            className="cancel-button"
            style={{
              backgroundColor: "#FF9800", // 🟠 ORANGE
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => doPost("cancel")}
            disabled={loading}
          >
            {loading ? "Cancelling..." : "Cancel"}
          </button>

          <button
            className="end-btn"
            style={{
              backgroundColor: "#f44336",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: "500",
              opacity: loading ? 0.7 : 1,
            }}
            onClick={() => {
              if (validateBeforeComplete?.() === false) return;
              doPost("complete");
            }}
            disabled={loading}
          >
            {loading ? "Completing..." : "Complete"}
          </button>
        </div>
      )}
    </div>
  );
};

export default Nurse3ActivePatientCard;
